import { createContext, type ReactNode, useCallback, useState } from 'react';

import {
	NOTIFY_LOGIN_USER_ACTION_QUERY_PARAM,
	OIDC_REDIRECT_URL_AUTH_CODE_QUERY_PARAM,
	OIDC_REDIRECT_URL_ERROR_QUERY_PARAM,
	OIDC_REDIRECT_URL_IS_LOGIN_QUERY_PARAM,
	PROTECTED_ROUTE_RETURN_PATH_QUERY_PARAM,
} from 'constants/queryParams';
import { useEffectOnce, useRequiredContext } from 'hooks';
import { useDeleteSearchParams, useSearchParams } from 'hooks/navigation';
import type { NotifyLoginUserActionType } from 'models/internalApi';
import type { OIDCError } from 'utils/business-logic';
import {
	exhaustiveArray,
	filterTruthyValues,
	objectKeys,
} from 'utils/collection';
import { empty } from 'utils/helpers';

interface InitialQueryValueContextType {
	[NOTIFY_LOGIN_USER_ACTION_QUERY_PARAM]?: NotifyLoginUserActionType;
	[OIDC_REDIRECT_URL_AUTH_CODE_QUERY_PARAM]?: string;
	[OIDC_REDIRECT_URL_ERROR_QUERY_PARAM]?: OIDCError;
	[OIDC_REDIRECT_URL_IS_LOGIN_QUERY_PARAM]?: string;
	[PROTECTED_ROUTE_RETURN_PATH_QUERY_PARAM]?: string;
}

export const InitialQueryValueContext = createContext<
	InitialQueryValueContextType | undefined
>(undefined);

interface Props {
	children: ReactNode;
}

function useGetQueryParams() {
	const searchParams = useSearchParams();

	return useCallback(() => {
		const queryParams: InitialQueryValueContextType = {};

		exhaustiveArray<keyof InitialQueryValueContextType>()(
			NOTIFY_LOGIN_USER_ACTION_QUERY_PARAM,
			OIDC_REDIRECT_URL_AUTH_CODE_QUERY_PARAM,
			OIDC_REDIRECT_URL_ERROR_QUERY_PARAM,
			OIDC_REDIRECT_URL_IS_LOGIN_QUERY_PARAM,
			PROTECTED_ROUTE_RETURN_PATH_QUERY_PARAM,
		).forEach((key) => {
			// @ts-expect-error we can't be asked to assert the result searchParams.get
			queryParams[key] = searchParams.get(key);
		});

		return filterTruthyValues(queryParams) ?? empty.object;
	}, [searchParams]);
}

/** Context with values read and deleted from the query on initial load, used mainly with redirect params */
export function InitialQueryValueContextProvider({ children }: Props) {
	const getQueryParams = useGetQueryParams();
	const { deleteSearchParams } = useDeleteSearchParams();

	const [queryParams] = useState(getQueryParams);
	useEffectOnce(() => {
		if (queryParams) {
			deleteSearchParams(objectKeys(queryParams));
		}
	});

	return (
		<InitialQueryValueContext value={queryParams}>
			{children}
		</InitialQueryValueContext>
	);
}
InitialQueryValueContextProvider.displayName =
	'InitialQueryValueContextProvider';

export function useInitialQueryValueContext() {
	return useRequiredContext(
		InitialQueryValueContext,
		'InitialQueryValueContext',
	);
}
